import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

// Stripe product/price mapping
export const STRIPE_PLANS = {
  starter: {
    name: "Starter",
    productId: "prod_T3tY0PKPUEDT0a",
    monthlyPriceId: "price_1S7ll8FMHIquSEIZDD54AXPX",
    yearlyPriceId: "price_1S7ll8FMHIquSEIZ64RiN6Qt",
    monthlyPrice: 99.90,
    yearlyPrice: 1078.92,
    features: ["1 usuário", "100 clientes", "Agenda básica", "Relatórios simples"],
  },
  professional: {
    name: "Professional",
    productId: "prod_T3tYo9Oj5Z0c6J",
    monthlyPriceId: "price_1S7ll9FMHIquSEIZ8NwDjPhA",
    yearlyPriceId: "price_1S7llAFMHIquSEIZLj04Tjd3",
    monthlyPrice: 199.90,
    yearlyPrice: 2158.92,
    features: ["5 usuários", "500 clientes", "Agenda avançada", "Relatórios completos", "Marketing"],
  },
  enterprise: {
    name: "Enterprise",
    productId: "prod_T3tYcYIvHASqbU",
    monthlyPriceId: "price_1S7llBFMHIquSEIZeNtONusP",
    yearlyPriceId: "price_1S7llBFMHIquSEIZgcSdELhM",
    monthlyPrice: 599.90,
    yearlyPrice: 6478.92,
    features: ["Usuários ilimitados", "Clientes ilimitados", "API access", "Suporte prioritário", "White label"],
  },
} as const;

export type PlanKey = keyof typeof STRIPE_PLANS;

interface StripeSubscription {
  subscribed: boolean;
  productId: string | null;
  priceId: string | null;
  subscriptionEnd: string | null;
  planKey: PlanKey | null;
  status: string | null;
  trialEnd: string | null;
  canceledAt: string | null;
}

export const useStripeSubscription = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<StripeSubscription>({
    subscribed: false,
    productId: null,
    priceId: null,
    subscriptionEnd: null,
    planKey: null,
    status: null,
    trialEnd: null,
    canceledAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkSubscription = useCallback(async () => {
    if (!user) {
      setSubscription({
        subscribed: false,
        productId: null,
        priceId: null,
        subscriptionEnd: null,
        planKey: null,
        status: null,
        trialEnd: null,
        canceledAt: null,
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fnError } = await supabase.functions.invoke("check-subscription");

      if (fnError) throw fnError;

      // Find plan key from product ID
      let planKey: PlanKey | null = null;
      if (data?.product_id) {
        for (const [key, plan] of Object.entries(STRIPE_PLANS)) {
          if (plan.productId === data.product_id) {
            planKey = key as PlanKey;
            break;
          }
        }
      }

      setSubscription({
        subscribed: data?.subscribed || false,
        productId: data?.product_id || null,
        priceId: data?.price_id || null,
        subscriptionEnd: data?.subscription_end || null,
        planKey,
        status: data?.status || null,
        trialEnd: data?.trial_end || null,
        canceledAt: data?.canceled_at || null,
      });
      setError(null);
    } catch (err) {
      console.error("Error checking subscription:", err);
      setError(err instanceof Error ? err.message : "Error checking subscription");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const createCheckout = async (priceId: string, couponCode?: string) => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke("create-checkout", {
        body: { priceId, couponCode },
      });

      if (fnError) throw fnError;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      console.error("Error creating checkout:", err);
      throw err;
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke("customer-portal");

      if (fnError) throw fnError;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      console.error("Error opening customer portal:", err);
      throw err;
    }
  };

  const cancelSubscription = async () => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke("cancel-subscription");

      if (fnError) throw fnError;
      await checkSubscription();
      return data;
    } catch (err) {
      console.error("Error canceling subscription:", err);
      throw err;
    }
  };

  return {
    subscription,
    loading,
    error,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    cancelSubscription,
  };
};
